import axios from "axios";

export const pwValid = (pw) => pw.length >= 8 && /[A-Za-zÇĞİÖŞÜçğıöşü]/.test(pw) && /\d/.test(pw);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

export const tokenStore = {
  get: () => localStorage.getItem("cl_token"),
  set: (t) => localStorage.setItem("cl_token", t),
  clear: () => localStorage.removeItem("cl_token"),
};

export const activeClient = {
  get: () => localStorage.getItem("cl_client") || "",
  set: (id) => localStorage.setItem("cl_client", id || ""),
  clear: () => localStorage.removeItem("cl_client"),
};

// attach bearer token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("cl_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// revoked/expired session anywhere -> back to login
const NO_KICK = ["/auth/login", "/auth/register", "/auth/change-password", "/auth/logout", "/auth/verify-email", "/auth/forgot-password", "/auth/reset-password"];
api.interceptors.response.use((r) => r, (err) => {
  const url = err.config?.url || "";
  if (err.response?.status === 401 && tokenStore.get() && !NO_KICK.some((p) => url.includes(p))) {
    tokenStore.clear(); activeClient.clear();
    window.location.assign("/login");
  }
  return Promise.reject(err);
});

const cparam = () => {
  const id = activeClient.get();
  return id ? { client_id: id } : {};
};

export const CL = {
  // auth
  register: (d) => api.post("/auth/register", d).then((r) => r.data),
  login: (d) => api.post("/auth/login", d).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data.user),
  logout: () => api.post("/auth/logout").then((r) => r.data),
  changePassword: (current_password, new_password) =>
    api.post("/auth/change-password", { current_password, new_password }).then((r) => r.data),

  // clients (workspaces)
  listClients: () => api.get("/clients").then((r) => r.data),
  createClient: (d) => api.post("/clients", d).then((r) => r.data),
  updateClient: (id, d) => api.put(`/clients/${id}`, d).then((r) => r.data),
  deleteClient: (id) => api.delete(`/clients/${id}`).then((r) => r.data),

  // entity (per active client)
  getEntity: () => api.get("/entity", { params: cparam() }).then((r) => r.data),
  updateEntity: (d) => api.put("/entity", d, { params: cparam() }).then((r) => r.data),

  // facilities
  listFacilities: () => api.get("/facilities", { params: cparam() }).then((r) => r.data),
  createFacility: (d) => api.post("/facilities", d, { params: cparam() }).then((r) => r.data),
  updateFacility: (id, d) => api.put(`/facilities/${id}`, d).then((r) => r.data),
  deleteFacility: (id) => api.delete(`/facilities/${id}`).then((r) => r.data),

  // factors
  listFactors: (scope) => api.get("/factors", { params: scope ? { scope } : {} }).then((r) => r.data),

  // activities
  listActivities: (scope) => api.get("/activities", { params: { ...cparam(), ...(scope ? { scope } : {}) } }).then((r) => r.data),
  createActivity: (d) => api.post("/activities", d, { params: cparam() }).then((r) => r.data),
  updateActivity: (id, d) => api.put(`/activities/${id}`, d).then((r) => r.data),
  deleteActivity: (id) => api.delete(`/activities/${id}`).then((r) => r.data),

  summary: () => api.get("/summary", { params: cparam() }).then((r) => r.data),

  reportBlob: () => api.get("/reports/pdf", { params: cparam(), responseType: "blob" }).then((r) => r.data),
  createShare: () => api.post("/reports/share", {}, { params: cparam() }).then((r) => r.data),
  publicReportUrl: (token) => `${process.env.REACT_APP_BACKEND_URL}/api/public/report/${token}`,

  getBranding: () => api.get("/branding").then((r) => r.data),
  updateBranding: (d) => api.put("/branding", d).then((r) => r.data),

  cbamList: (year, quarter) => api.get("/cbam", { params: { ...cparam(), year, quarter } }).then((r) => r.data),
  cbamCreate: (d, year, quarter) => api.post("/cbam", { ...d, year, quarter }, { params: cparam() }).then((r) => r.data),
  cbamUpdate: (id, d) => api.put(`/cbam/${id}`, d).then((r) => r.data),
  cbamDelete: (id) => api.delete(`/cbam/${id}`).then((r) => r.data),
  cbamSummary: (year, quarter) => api.get("/cbam/summary", { params: { ...cparam(), year, quarter } }).then((r) => r.data),
  cbamTemplate: () => api.get("/cbam/template.xlsx", { responseType: "blob" }).then((r) => r.data),
  cbamImport: (file) => { const fd = new FormData(); fd.append("file", file); return api.post("/cbam/import", fd, { params: cparam() }).then((r) => r.data); },
  supReqList: () => api.get("/cbam/supplier-requests", { params: cparam() }).then((r) => r.data),
  supReqCreate: (d) => api.post("/cbam/supplier-requests", d, { params: cparam() }).then((r) => r.data),
  supReqApprove: (id) => api.post(`/cbam/supplier-requests/${id}/approve`).then((r) => r.data),
  supReqReject: (id) => api.post(`/cbam/supplier-requests/${id}/reject`).then((r) => r.data),
  supReqDelete: (id) => api.delete(`/cbam/supplier-requests/${id}`).then((r) => r.data),
  supplierInbox: () => api.get("/supplier/requests").then((r) => r.data),
  supplierResendVerification: () => api.post("/auth/supplier/send-verification").then((r) => r.data),
  verifyEmail: (token) => api.post("/auth/verify-email", { token }).then((r) => r.data),
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }).then((r) => r.data),
  resetPassword: (token, new_password) => api.post("/auth/reset-password", { token, new_password }).then((r) => r.data),
  supplierSubmit: (id, d) => api.post(`/supplier/requests/${id}/submit`, d).then((r) => r.data),
  cbamCountries: () => api.get("/cbam/reference/countries").then((r) => r.data),
  cbamGoods: (country) => api.get("/cbam/reference/goods", { params: { country } }).then((r) => r.data),
  cbamLookup: (country, cn) => api.get("/cbam/reference/lookup", { params: { country, cn } }).then((r) => r.data),
  cbamExportBlob: (kind, year, quarter) => api.get(`/cbam/export.${kind}`, { params: { ...cparam(), year, quarter }, responseType: "blob" }).then((r) => r.data),
  cbamExportXmlText: (year, quarter) => api.get(`/cbam/export.xml`, { params: { ...cparam(), year, quarter }, responseType: "text", transformResponse: [(d) => d] }).then((r) => r.data),

  getTargets: () => api.get("/targets", { params: cparam() }).then((r) => r.data),
  putTargets: (d) => api.put("/targets", d, { params: cparam() }).then((r) => r.data),
  getIntensity: () => api.get("/intensity", { params: cparam() }).then((r) => r.data),
  getVerification: () => api.get("/verification", { params: cparam() }).then((r) => r.data),
  putVerification: (d) => api.put("/verification", d, { params: cparam() }).then((r) => r.data),
  getCarbonTax: () => api.get("/carbon-tax", { params: cparam() }).then((r) => r.data),
  getEsrs: () => api.get("/esrs", { params: cparam() }).then((r) => r.data),
  putEsrs: (d) => api.put("/esrs", d, { params: cparam() }).then((r) => r.data),
  getGri: () => api.get("/gri", { params: cparam() }).then((r) => r.data),
  putGri: (d) => api.put("/gri", d, { params: cparam() }).then((r) => r.data),
  getIfrsS1: () => api.get("/ifrs-s1", { params: cparam() }).then((r) => r.data),
  putIfrsS1: (d) => api.put("/ifrs-s1", d, { params: cparam() }).then((r) => r.data),
  getIfrsS2: () => api.get("/ifrs-s2", { params: cparam() }).then((r) => r.data),
  putIfrsS2: (d) => api.put("/ifrs-s2", d, { params: cparam() }).then((r) => r.data),
  createLead: (d) => api.post("/leads", d).then((r) => r.data),
};

export default api;
